/**
 * Sugar Town Commerce — Discovery, merchandising & personalization (Phase 4).
 *
 * Reads the merchandising controls (featured / staff picks / best sellers /
 * new arrivals / seasonal collections), runs metadata-driven recommendation
 * shelves, tracks recently viewed products locally, and surfaces personalized
 * shelves for signed-in customers. Shopify is never referenced.
 */
import { supabase } from "@/integrations/supabase/client";
import type { CommerceProduct, CommerceCollection } from "./types";
import { getCatalogSnapshot, type SearchableProduct } from "./search";
import { getProductsByIds } from "./api";

type Json = Record<string, unknown>;

// ---------- Merchandising shelves ----------

export interface MerchandisingRule {
  id: string;
  key: string;
  title: string;
  subtitle: string | null;
  description: string | null;
  rule_type: string;
  match: Json;
  product_ids: string[];
  collection_slug: string | null;
  image_url: string | null;
  cta_label: string | null;
  cta_url: string | null;
  max_items: number;
  sort_order: number;
}

export async function getFeaturedProducts(limit = 8): Promise<SearchableProduct[]> {
  const catalog = await getCatalogSnapshot();
  return catalog
    .filter((p) => p.is_featured)
    .sort((a, b) => (a.merch_pin_order ?? 9999) - (b.merch_pin_order ?? 9999) || (b.sales_count ?? 0) - (a.sales_count ?? 0))
    .slice(0, limit);
}

export async function getStaffPicks(limit = 8): Promise<SearchableProduct[]> {
  const catalog = await getCatalogSnapshot();
  return catalog.filter((p) => p.is_staff_pick).slice(0, limit);
}

export async function getNewArrivals(limit = 8): Promise<SearchableProduct[]> {
  const catalog = await getCatalogSnapshot();
  return [...catalog].sort((a, b) => +new Date(b.created_at) - +new Date(a.created_at)).slice(0, limit);
}

export async function getBestSellers(limit = 8): Promise<SearchableProduct[]> {
  const catalog = await getCatalogSnapshot();
  const ranked = [...catalog].sort((a, b) => (b.sales_count ?? 0) - (a.sales_count ?? 0));
  // If no sales data yet, fall back to featured/newest so the shelf isn't empty.
  if (ranked.every((p) => (p.sales_count ?? 0) === 0)) {
    return getFeaturedProducts(limit).then((f) => (f.length ? f : ranked.slice(0, limit)));
  }
  return ranked.slice(0, limit);
}

/** Collections to show on the home/shop landing, respecting schedule windows. */
export async function getHomeCollections(): Promise<CommerceCollection[]> {
  const now = new Date().toISOString();
  const { data, error } = await supabase
    .from("commerce_collections")
    .select("*")
    .eq("is_active", true)
    .eq("show_on_home", true)
    .order("sort_order", { ascending: true });
  if (error) throw error;
  return (data ?? []).filter(
    (c) => (!c.starts_at || c.starts_at <= now) && (!c.ends_at || c.ends_at >= now)
  );
}

/** Currently-live seasonal collections. */
export async function getSeasonalCollections(): Promise<CommerceCollection[]> {
  const now = new Date().toISOString();
  const { data, error } = await supabase
    .from("commerce_collections")
    .select("*")
    .eq("is_active", true)
    .eq("is_seasonal", true)
    .order("sort_order", { ascending: true });
  if (error) throw error;
  return (data ?? []).filter(
    (c) => (!c.starts_at || c.starts_at <= now) && (!c.ends_at || c.ends_at >= now)
  );
}

// ---------- Recommendation rules (metadata-driven) ----------

export async function getMerchandisingRules(ruleType?: string): Promise<MerchandisingRule[]> {
  const now = new Date().toISOString();
  let q = supabase.from("commerce_merchandising_rules").select("*").eq("is_active", true);
  if (ruleType) q = q.eq("rule_type", ruleType);
  const { data, error } = await q.order("sort_order", { ascending: true });
  if (error) throw error;
  return ((data ?? []) as unknown as MerchandisingRule[]).filter(
    (r) => (!(r as any).starts_at || (r as any).starts_at <= now) && (!(r as any).ends_at || (r as any).ends_at >= now)
  );
}

function arr(v: unknown): string[] {
  return Array.isArray(v) ? (v as string[]) : [];
}

/** Resolve the products for a merchandising rule (curated ids first, then metadata match). */
export async function resolveRuleProducts(rule: MerchandisingRule): Promise<SearchableProduct[]> {
  const catalog = await getCatalogSnapshot();
  const byId = new Map(catalog.map((p) => [p.id, p]));

  // 1. Hand-picked products win.
  const curated = rule.product_ids.map((id) => byId.get(id)).filter((p): p is SearchableProduct => Boolean(p));
  if (curated.length >= rule.max_items) return curated.slice(0, rule.max_items);

  // 2. Collection-based.
  let pool = catalog;
  if (rule.collection_slug) {
    const { data: col } = await supabase
      .from("commerce_collections")
      .select("id")
      .eq("slug", rule.collection_slug)
      .maybeSingle();
    if (col) {
      const { data: links } = await supabase
        .from("commerce_product_collections")
        .select("product_id")
        .eq("collection_id", col.id);
      const ids = new Set((links ?? []).map((l) => l.product_id));
      pool = catalog.filter((p) => ids.has(p.id));
    }
  }

  // 3. Metadata match scoring.
  const m = rule.match || {};
  const wantGoals = arr(m.wellness_goals);
  const wantConcerns = arr(m.concerns);
  const wantIngredients = arr(m.ingredient_tags);
  const wantTypes = arr(m.product_types);
  const wantStages = arr(m.ritual_stages);
  const wantCustomers = arr(m.suitable_customer_types);
  const wantSeasonality = arr(m.seasonality);
  const wantUseCases = arr(m.lifestyle_use_cases);

  const scoreOf = (p: SearchableProduct) => {
    const overlap = (a: string[], b: string[]) => a.filter((x) => b.includes(x)).length;
    return (
      overlap(arr(p.wellness_goals), wantGoals) * 3 +
      overlap(arr(p.concerns), wantConcerns) * 3 +
      overlap(arr(p.ingredient_tags), wantIngredients) * 2 +
      overlap([p.product_type], wantTypes) * 2 +
      overlap([p.ritual_stage ?? ""], wantStages) * 2 +
      overlap(arr(p.suitable_customer_types), wantCustomers) * 2 +
      overlap(arr(p.seasonality), wantSeasonality) +
      overlap(arr(p.lifestyle_use_cases), wantUseCases)
    );
  };

  const curatedIds = new Set(curated.map((p) => p.id));
  const matched = pool
    .filter((p) => !curatedIds.has(p.id))
    .map((p) => ({ p, s: scoreOf(p) }))
    .filter((x) => x.s > 0)
    .sort((a, b) => b.s - a.s)
    .map((x) => x.p);

  return [...curated, ...matched].slice(0, rule.max_items);
}

// ---------- Recently viewed (local) ----------

const RV_KEY = "sugar-town-recently-viewed";
const RV_MAX = 12;

export function trackProductView(productId: string) {
  try {
    const raw = localStorage.getItem(RV_KEY);
    const ids: string[] = raw ? JSON.parse(raw) : [];
    const next = [productId, ...ids.filter((id) => id !== productId)].slice(0, RV_MAX);
    localStorage.setItem(RV_KEY, JSON.stringify(next));
  } catch {
    /* ignore storage errors */
  }
}

export function getRecentlyViewedIds(excludeId?: string): string[] {
  try {
    const raw = localStorage.getItem(RV_KEY);
    const ids: string[] = raw ? JSON.parse(raw) : [];
    return excludeId ? ids.filter((id) => id !== excludeId) : ids;
  } catch {
    return [];
  }
}

export async function getRecentlyViewedProducts(excludeId?: string, limit = 8): Promise<CommerceProduct[]> {
  const ids = getRecentlyViewedIds(excludeId).slice(0, limit);
  if (ids.length === 0) return [];
  return getProductsByIds(ids);
}

// ---------- Personalization (signed-in) ----------

/** Product ids the signed-in customer has previously purchased (paid orders). */
export async function getPurchasedProductIds(): Promise<string[]> {
  const { data: auth } = await supabase.auth.getUser();
  if (!auth.user) return [];
  const { data: orders } = await supabase
    .from("commerce_orders")
    .select("id")
    .in("status", ["paid", "fulfilled", "partially_fulfilled"]);
  const orderIds = (orders ?? []).map((o) => o.id);
  if (orderIds.length === 0) return [];
  const { data: items } = await supabase
    .from("commerce_order_items")
    .select("product_id")
    .in("order_id", orderIds);
  return Array.from(new Set((items ?? []).map((i) => i.product_id).filter(Boolean) as string[]));
}

export async function getPreviouslyPurchased(limit = 8): Promise<CommerceProduct[]> {
  const ids = await getPurchasedProductIds();
  if (ids.length === 0) return [];
  return (await getProductsByIds(ids)).slice(0, limit);
}

/**
 * "Complete your routine" / "You may also enjoy" — recommends products that
 * share wellness goals, concerns or ritual stages with what the customer has
 * already engaged with (purchased + recently viewed), excluding those items.
 */
export async function getPersonalizedRecommendations(limit = 8): Promise<SearchableProduct[]> {
  const catalog = await getCatalogSnapshot();
  const byId = new Map(catalog.map((p) => [p.id, p]));

  const purchased = await getPurchasedProductIds();
  const viewed = getRecentlyViewedIds();
  const seedIds = Array.from(new Set([...purchased, ...viewed]));
  const seeds = seedIds.map((id) => byId.get(id)).filter((p): p is SearchableProduct => Boolean(p));
  if (seeds.length === 0) return [];

  const goals = new Set(seeds.flatMap((p) => arr(p.wellness_goals)));
  const concerns = new Set(seeds.flatMap((p) => arr(p.concerns)));
  const stages = new Set(seeds.flatMap((p) => [p.ritual_stage].filter(Boolean) as string[]));
  const pairIds = new Set(seeds.flatMap((p) => arr(p.pair_with_product_ids)));
  const exclude = new Set(seedIds);

  const scored = catalog
    .filter((p) => !exclude.has(p.id))
    .map((p) => {
      let s = 0;
      if (pairIds.has(p.id)) s += 6;
      s += arr(p.wellness_goals).filter((g) => goals.has(g)).length * 3;
      s += arr(p.concerns).filter((c) => concerns.has(c)).length * 3;
      if (p.ritual_stage && stages.has(p.ritual_stage)) s += 2;
      return { p, s };
    })
    .filter((x) => x.s > 0)
    .sort((a, b) => b.s - a.s)
    .map((x) => x.p);

  return scored.slice(0, limit);
}

// ---------- Editorial landing pages ----------

export interface LandingPageSection {
  heading?: string;
  body?: string;
  image_url?: string;
  collection_slug?: string;
  product_ids?: string[];
}

export interface LandingPage {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  intro: string | null;
  hero_image_url: string | null;
  sections: LandingPageSection[];
  product_ids: string[];
  collection_slug: string | null;
  seo_title: string | null;
  seo_description: string | null;
}

export async function getLandingPage(slug: string): Promise<LandingPage | null> {
  const { data, error } = await supabase
    .from("commerce_landing_pages")
    .select("*")
    .eq("slug", slug)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  return {
    ...(data as any),
    sections: Array.isArray((data as any).sections) ? (data as any).sections : [],
    product_ids: (data as any).product_ids ?? [],
  } as LandingPage;
}
